import InstrumentFacade, {
  BlockEnum,
  InstrumentModelEnum,
  InstrumentStatusEnum
} from '../common/InstrumentFacade';
import { ImmutableMap, InstrumentItem } from '../../frontend-common-libs/src/common/types';
import { LoadedState, ReservationState } from './InstrumentFleetRow';
import reservedIcon from '../../frontend-common-libs/src/fleet-management/assets/reserved-status.svg';
import availableIcon from '../../frontend-common-libs/src/fleet-management/assets/available-status.svg';
import unavailableIcon from '../../frontend-common-libs/src/fleet-management/assets/unavailable-status.svg';
import connectingIcon from '../assets/connecting-status.svg';
import { ReservationSectionsDesiredBlock } from '../../frontend-common-libs/src/fleet-management/Reservation';

export const ReservationStatusEnum = {
  Reserved: 'Reserved',
  Available: 'Available',
  Unavailable: 'Unavailable',
  ReservationPending: 'Reservation pending',
  CancellationPending: 'Cancellation pending'
};

export type Props = {
  instrument: ImmutableMap<InstrumentItem>;
  reservationState: ReservationState;
  userId: string;
  fleetRowShadowsLoaded: LoadedState;
  block?: BlockEnum;
};

export const MinimumSupportedMajorVersion = 3;

export const RESERVATION_EXPIRATION_WINDOW = 5 * 60000;

export default class ReservationFacade {
  private readonly devSupportedMajorVersion = 0;

  private readonly localInstrumentFacade: InstrumentFacade;

  private readonly reservationState: ReservationState;

  private readonly userId: string;

  private readonly fleetRowShadowsLoaded: LoadedState;

  public constructor(props: Props) {
    const { instrument, reservationState, userId, fleetRowShadowsLoaded, block } = props;
    this.reservationState = reservationState;
    this.userId = userId;
    this.fleetRowShadowsLoaded = fleetRowShadowsLoaded;
    this.localInstrumentFacade = new InstrumentFacade(instrument, block);
  }

  public get isInstrumentReservedByUser(): boolean {
    return this.isReserved && this.userId === this.reservationState.reservationShadowState?.userId;
  }

  private get hasReservationShadow(): boolean {
    return this.reservationState.reservationShadowState != null;
  }

  private get isReserved(): boolean {
    return this.reservationState.reservationShadowState?.reserved ?? false;
  }

  private get isNotReserved(): boolean {
    return !this.isReserved;
  }

  private get isSomeoneLoggedIn(): boolean {
    return this.reservationState.reservationShadowState?.loggedIn ?? false;
  }

  private get noOneIsLoggedIn(): boolean {
    return !this.isSomeoneLoggedIn;
  }

  private get isIdle(): boolean {
    return this.reservationState.instrumentStatus === InstrumentStatusEnum.Idle;
  }

  private get hasInstrumentSection(): boolean {
    return !(
      this.reservationState.reservationShadowState === null ||
      this.reservationState.reservationShadowState?.loggedIn === null
    );
  }

  public get isInstrumentReservable(): boolean {
    return (
      this.hasReservationShadow &&
      this.hasInstrumentSection &&
      !this.isReservationPending() &&
      !this.isCancellationPending() &&
      this.isNotReserved &&
      this.noOneIsLoggedIn &&
      this.isIdle
    );
  }

  private get desiredBlock(): ReservationSectionsDesiredBlock | undefined {
    return this.reservationState.reservationShadowState?.desired;
  }

  public isReservationPending(): boolean {
    let someoneWantsToReserveIt = false;
    if (this.desiredBlock) {
      someoneWantsToReserveIt =
        !!this.desiredBlock.userId &&
        this.desiredBlock.userId !== '' &&
        this.desiredBlock.reserved === true;
    }
    return someoneWantsToReserveIt && this.isNotReserved;
  }

  public isCancellationPending(): boolean {
    let someoneWantsToCancel = false;
    if (this.desiredBlock) {
      someoneWantsToCancel =
        this.desiredBlock.userId === '' && this.desiredBlock.reserved === false;
    }
    return this.isReserved && someoneWantsToCancel;
  }

  private currentUserWantsToReserveIt(): boolean {
    if (this.desiredBlock) {
      return this.desiredBlock.userId === this.userId;
    }
    return false;
  }

  public get displayedReservationStatus(): string {
    if (this.isCancellationPending()) {
      if (this.isInstrumentReservedByUser) {
        return ReservationStatusEnum.CancellationPending;
      }
      return ReservationStatusEnum.Unavailable;
    }
    if (this.isInstrumentReservedByUser) {
      return ReservationStatusEnum.Reserved;
    }
    if (this.isReservationPending()) {
      if (this.currentUserWantsToReserveIt()) {
        return ReservationStatusEnum.ReservationPending;
      }
      return ReservationStatusEnum.Unavailable;
    }
    if (this.isInstrumentReservable) {
      return ReservationStatusEnum.Available;
    }
    return ReservationStatusEnum.Unavailable;
  }

  private static getMajorVersion(softwareVersion: string | undefined): number {
    if (softwareVersion) {
      const currentVersion = softwareVersion.split('.');
      const parsedVersion = parseInt(currentVersion[0], 10);
      if (Number.isInteger(parsedVersion)) {
        return parsedVersion;
      }
    }
    return -1;
  }

  private static supportsReleaseReservation(softwareVersion: string | undefined): boolean {
    return this.getMajorVersion(softwareVersion) >= MinimumSupportedMajorVersion;
  }

  private supportsDevelopmentReservation(softwareVersion: string | undefined): boolean {
    return ReservationFacade.getMajorVersion(softwareVersion) === this.devSupportedMajorVersion;
  }

  public softwareVersionSupportsReservations(softwareVersion: string | undefined): boolean {
    return (
      ReservationFacade.supportsReleaseReservation(softwareVersion) ||
      this.supportsDevelopmentReservation(softwareVersion)
    );
  }

  public modelSupportsReservations(): boolean {
    return this.localInstrumentFacade.instrumentModel !== InstrumentModelEnum.PTCTempo4848;
  }

  public get instrumentFacade(): InstrumentFacade {
    return this.localInstrumentFacade;
  }

  public get isAllReservationShadowsLoaded(): boolean {
    return (
      this.fleetRowShadowsLoaded.instrumentStatusLoaded &&
      this.fleetRowShadowsLoaded.reservationShadowLoaded
    );
  }

  public get reservationStatusProperties() {
    if (this.isAllReservationShadowsLoaded) {
      if (this.isCancellationPending()) {
        if (this.isInstrumentReservedByUser) {
          return { src: reservedIcon, alt: 'cancellation pending' };
        }
        return { src: unavailableIcon, alt: 'unavailable' };
      }
      if (this.isInstrumentReservedByUser) {
        return { src: reservedIcon, alt: 'reserved' };
      }
      if (this.isReservationPending()) {
        if (this.currentUserWantsToReserveIt()) {
          return { src: reservedIcon, alt: 'reservation pending' };
        }
        return { src: unavailableIcon, alt: 'unavailable' };
      }
      if (this.isInstrumentReservable) {
        return { src: availableIcon, alt: 'available' };
      }
      return { src: unavailableIcon, alt: 'unavailable' };
    }
    return { src: connectingIcon, alt: 'connecting' };
  }

  private reservationTimeout(): number {
    const reservationRequestTimestamp = this.desiredBlock?.metadata?.reserved ?? 0;
    const requestTimestampInMs = reservationRequestTimestamp * 1000;
    const timeout = RESERVATION_EXPIRATION_WINDOW - (Date.now() - requestTimestampInMs);
    if (timeout > 0) {
      return timeout;
    }
    return 0;
  }

  public reservationPendingTimeout(): number | null {
    if (this.isReservationPending()) {
      return this.reservationTimeout();
    }
    return null;
  }

  public cancellationPendingTimeout(): number | null {
    if (this.isCancellationPending()) {
      return this.reservationTimeout();
    }
    return null;
  }
}
